// ============================================================
// 运行时适配层 runtime.ts
// 统一屏蔽 Electron(桌面) vs Web(浏览器) 的能力差异
// 前端代码一律通过 import { runtime } from './runtime' 调用
// ============================================================

import type { DbProxy } from './db';  // 仅类型引用（已废弃，保留避免 import 报错）

// ================ 模式检测 ================
const UA = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
const hasElectronAPI = typeof window !== 'undefined' && !!(window as any).electronAPI;
export const RUNTIME_MODE: 'electron' | 'web' = hasElectronAPI ? 'electron' : 'web';
export const IS_ELECTRON = RUNTIME_MODE === 'electron';
export const IS_WEB = RUNTIME_MODE === 'web';

// ================ Setting 读写 ================
// Electron：electronAPI.getSetting / setSetting
// Web    ：优先 sessionStorage 缓存，再走后端 API（M3 阶段会补全后端 API，
//          未上线之前用 localStorage 作为过渡，保证代码能跑通）
export async function getSetting(key: string): Promise<string> {
  if (IS_ELECTRON) {
    return await (window as any).electronAPI.getSetting(key);
  }
  try {
    // 1) session 缓存（来自登录时的 merchantConfig）
    const cached = sessionStorage.getItem('setting_' + key);
    if (cached !== null) return cached;
    // 2) 尝试用远程 API（M3 上线后生效；未上线的 API 返回 404 不影响）
    const token = await getRemoteTokenSafe();
    const base = getRemoteApiBase();
    if (token && base) {
      try {
        const resp = await fetch(`${base}/api/merchant/setting/${encodeURIComponent(key)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.ok) {
          const d = await resp.json().catch(() => null);
          const v = String(d?.value ?? '');
          sessionStorage.setItem('setting_' + key, v);
          return v;
        }
      } catch (_) {}
    }
    // 3) 兜底：localStorage（Web 版临时兼容方案，M3 同步能力上线后可移除）
    const fallback = localStorage.getItem('setting_' + key);
    return fallback ?? '';
  } catch (_) { return ''; }
}

export async function setSetting(key: string, value: string): Promise<void> {
  if (IS_ELECTRON) {
    await (window as any).electronAPI.setSetting(key, value);
    return;
  }
  try {
    sessionStorage.setItem('setting_' + key, value);
    localStorage.setItem('setting_' + key, value);
    const token = await getRemoteTokenSafe();
    const base = getRemoteApiBase();
    if (token && base) {
      try {
        await fetch(`${base}/api/merchant/setting`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ key, value })
        });
      } catch (_) {}
    }
  } catch (_) {}
}

// ================ 快递查询 ================
export async function queryExpress(expressNo: string, phone?: string): Promise<any> {
  if (IS_ELECTRON) {
    const appCode = await getSetting('express_appcode');
    const apiUrl = await getSetting('express_api_url');
    return await (window as any).electronAPI.queryExpress(appCode, apiUrl, expressNo, phone);
  }
  // Web 模式：走后端代理（M3 上线，失败时返回空）
  const token = await getRemoteTokenSafe();
  const base = getRemoteApiBase();
  try {
    const u = new URL(`${base}/api/express/query`);
    u.searchParams.set('no', expressNo);
    if (phone) u.searchParams.set('phone', phone);
    const r = await fetch(u.toString(), token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
    if (!r.ok) return { success: false, error: `HTTP ${r.status}` };
    return await r.json();
  } catch (e: any) {
    return { success: false, error: e?.message || '网络异常' };
  }
}

// ================ 企微 Webhook ================
export async function sendWecomWebhook(webhookUrl: string, content: string): Promise<{ success: boolean; error?: string }> {
  if (IS_ELECTRON) {
    return await (window as any).electronAPI.sendWecomWebhook(webhookUrl, content);
  }
  const token = await getRemoteTokenSafe();
  const base = getRemoteApiBase();
  try {
    const r = await fetch(`${base}/api/webhook/wecom`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ webhookUrl, content })
    });
    const data = await r.json().catch(() => ({ success: r.ok }));
    return data || { success: r.ok };
  } catch (e: any) {
    return { success: false, error: e?.message };
  }
}

export async function sendWecomImageWebhook(webhookUrl: string, base64: string): Promise<{ success: boolean; error?: string }> {
  if (IS_ELECTRON) {
    return await (window as any).electronAPI.sendWecomImageWebhook(webhookUrl, base64);
  }
  const token = await getRemoteTokenSafe();
  const base = getRemoteApiBase();
  try {
    const r = await fetch(`${base}/api/webhook/wecom-image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ webhookUrl, base64 })
    });
    const data = await r.json().catch(() => ({ success: r.ok }));
    return data || { success: r.ok };
  } catch (e: any) {
    return { success: false, error: e?.message };
  }
}

// ================ 图片存储（附件/海报） ================
// Electron: electronAPI.saveImage/readImage（本地目录）
// Web:      全部走后端 upload-image / REMOTE_API_URL/uploads/
export async function saveImage(category: string, filename: string, data: string): Promise<string> {
  if (IS_ELECTRON) {
    const r = await (window as any).electronAPI.saveImage(category, filename, data);
    return typeof r === 'string' ? r : (r?.relPath || r?.path || '');
  }
  // Web 模式：上传到后端，返回相对路径 /uploads/xxx
  const token = await getRemoteTokenSafe();
  const base = getRemoteApiBase();
  const [metaB64, contentB64] = data.split(',');
  const mimeType = (metaB64.match(/:(.*?);base64/) || [])[1] || 'image/png';
  const binary = Uint8Array.from(atob(contentB64), c => c.charCodeAt(0));
  const blob = new Blob([binary], { type: mimeType });
  const fd = new FormData();
  fd.append('file', blob, filename);
  fd.append('category', category || 'other');
  const r = await fetch(`${base}/api/upload-image`, {
    method: 'POST',
    body: fd,
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  });
  const d = await r.json().catch(() => ({}));
  return d?.relPath || d?.url || d?.path || '';
}

export async function readImage(relPath: string): Promise<string | null> {
  if (IS_ELECTRON) {
    const r = await (window as any).electronAPI.readImage(relPath);
    return r?.data || r?.base64 || (typeof r === 'string' ? r : null);
  }
  const base = getRemoteApiBase();
  try {
    const url = relPath?.startsWith('http') ? relPath : `${base}${relPath}`;
    const r = await fetch(url);
    if (!r.ok) return null;
    const blob = await r.blob();
    const reader = new FileReader();
    return new Promise(resolve => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (_) { return null; }
}

export async function existsImage(relPath: string): Promise<boolean> {
  if (IS_ELECTRON) return !!(await (window as any).electronAPI.existsImage?.(relPath));
  const base = getRemoteApiBase();
  try {
    const url = relPath?.startsWith('http') ? relPath : `${base}${relPath}`;
    const r = await fetch(url, { method: 'HEAD' });
    return r.ok;
  } catch (_) { return false; }
}

export async function deleteImage(relPath: string): Promise<void> {
  if (IS_ELECTRON) { await (window as any).electronAPI.deleteImage?.(relPath); return; }
  const token = await getRemoteTokenSafe();
  const base = getRemoteApiBase();
  try {
    await fetch(`${base}/api/delete-image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ relPath })
    });
  } catch (_) {}
}

// ================ 打印能力 ================
export async function getPrinters(): Promise<Array<{ name: string }>> {
  if (IS_ELECTRON) return (await (window as any).electronAPI.getPrinters?.()) || [];
  // Web 模式：浏览器原生没枚举打印机的 API，返回空数组，UI 会降级为"打印预览"
  return [];
}

export async function printWaybill(htmlContent: string, opts?: { silent?: boolean; printBackground?: boolean }): Promise<{ success: boolean; error?: string }> {
  if (IS_ELECTRON) {
    const r = await (window as any).electronAPI.printWaybill?.(htmlContent, opts);
    if (typeof r === 'boolean') return { success: r };
    if (r && typeof r === 'object') return r;
    return { success: !!r };
  }
  // Web 模式：打开新窗口 + window.print()
  try {
    const w = window.open('', '_blank', 'width=800,height=1100');
    if (!w) return { success: false, error: '浏览器阻止了新窗口，请允许弹窗' };
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>打印面单</title></head><body>${htmlContent}</body></html>`);
    w.document.close();
    setTimeout(() => {
      try { w.print(); } catch (_) {}
    }, 400);
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e?.message };
  }
}

// ================ 其他能力 ================
export async function openExternal(url: string): Promise<void> {
  if (IS_ELECTRON) await (window as any).electronAPI.openExternal?.(url);
  else window.open(url, '_blank', 'noopener');
}

export function showNotification(title: string, body: string): void {
  if (IS_ELECTRON) {
    (window as any).electronAPI?.showNotification?.(title, body);
    return;
  }
  try {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification(title, { body });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(p => { if (p === 'granted') new Notification(title, { body }); });
      }
    }
  } catch (_) {}
}

export async function getUserDataPath(): Promise<string> {
  if (IS_ELECTRON) return (await (window as any).electronAPI.getUserDataPath?.()) || '';
  return 'localStorage://yangpin-merchant';
}

export async function showOpenDialog(options?: any): Promise<{ filePaths?: string[]; canceled: boolean }> {
  if (IS_ELECTRON) {
    const r = await (window as any).electronAPI.showOpenDialog?.(options);
    return r || { canceled: true };
  }
  // Web：通过不可见 input 模拟
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    if (options?.properties?.includes('multiSelections')) input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);
    input.onchange = () => {
      const files = Array.from(input.files || []);
      document.body.removeChild(input);
      resolve({ filePaths: files.map(f => f.name), canceled: false });
    };
    input.click();
    setTimeout(() => { if (input.parentNode) { document.body.removeChild(input); resolve({ canceled: true }); } }, 60_000);
  });
}

export function loginSuccess(): void {
  if (IS_ELECTRON) (window as any).electronAPI?.loginSuccess?.();
  // Web 模式：无需特殊处理
}

export function getAppVersion(): string {
  if (IS_ELECTRON) {
    try { return (window as any).electronAPI?.getVersion?.() ?? (window as any).electronAPI?.getAppVersion?.() ?? '1.0.0'; }
    catch (_) { return '1.0.0'; }
  }
  // Web：从 meta/path 判断，先返回固定
  try {
    const m = document.querySelector('meta[name="app-version"]') as HTMLMetaElement | null;
    return m?.content || 'web-1.0.0';
  } catch (_) { return 'web-1.0.0'; }
}

// 备份相关：Web 模式下全部走云端接口（M3 阶段补全，先做不报错的空实现）
export const backup = {
  async backupNow() {
    if (IS_ELECTRON) return (window as any).electronAPI.backupNow?.();
    // Web 模式：触发一次 db.flushSave 并派发事件
    // db.flushSave() 已废弃
    return { success: true, mode: 'web-idb-only' };
  },
  async backupList() {
    if (IS_ELECTRON) return (window as any).electronAPI.backupList?.() || [];
    return [];
  },
  async backupRestore(fileName: string) {
    if (IS_ELECTRON) return (window as any).electronAPI.backupRestore?.(fileName);
    return { success: false, error: 'Web 模式暂不支持此操作，请使用桌面端' };
  },
  async backupDelete(fileName: string) {
    if (IS_ELECTRON) return (window as any).electronAPI.backupDelete?.(fileName);
    return undefined;
  },
  async saveConfig(config: any) {
    if (IS_ELECTRON) return (window as any).electronAPI.backupSaveConfig?.(config);
    localStorage.setItem('backup_config', JSON.stringify(config));
  },
  async getConfig(): Promise<any> {
    if (IS_ELECTRON) return (window as any).electronAPI.backupGetConfig?.() || {};
    try { return JSON.parse(localStorage.getItem('backup_config') || '{}'); }
    catch (_) { return {}; }
  }
};

// ================ 内部辅助方法 ================
// 导出原始 electronAPI 引用（仅 IS_ELECTRON 时非空），给 App.tsx / Sidebar.tsx 中 checkForUpdates 系列用法使用
// Web 模式下提供一个"空函数桩"对象，保证 api.onUpdateAvailable() 等调用不报 TypeError
const _WEB_STUB_ELECTRON_API: any = new Proxy(
  {
    // 常用的"主动返回"方法：Web 版无 Electron 自动更新/打印/对话框等，都直接 no-op
    getVersion: () => { try { return getAppVersion(); } catch(_) { return '1.0.0'; } },
    getAppVersion: () => { try { return getAppVersion(); } catch(_) { return '1.0.0'; } },
    checkForUpdates: async () => ({}),
    downloadUpdate: async () => ({}),
    installUpdate: () => {},
    printWaybill: async () => ({ success: false, error: 'Web 模式不支持本地打印，请截图后使用浏览器打印' }),
    showNotification: (t: string, b: string) => showNotification(t, b),
    openExternal: (u: string) => openExternal(u),
    showOpenDialog: async () => ({ filePaths: [], canceled: true }),
    getPrinters: async () => [],
    getSetting: (k: string) => getSetting(k),
    setSetting: (k: string, v: string) => setSetting(k, v),
    queryExpress: async () => null,
    existsImage: async () => false,
    saveImage: async () => ({ success: false, url: '', id: '' }),
    readImage: async () => null,
    deleteImage: async () => false,
    getUserDataPath: () => '/'
  },
  {
    get(target: any, prop: any, _rec: any) {
      if (prop in target) return target[prop];
      // 任何其他未定义属性：如果是注册回调的 onXxx 方法，返回 no-op callback 接受器
      if (typeof prop === 'string' && prop.startsWith('on')) {
        return (_cb: any) => { /* Web 版无此事件源，忽略注册 */ };
      }
      // 其他未定义方法：返回 no-op 函数，避免调用时崩溃
      return (..._args: any[]) => undefined;
    }
  }
);
export const _electronApi: any =
  (typeof window !== 'undefined' && (window as any).electronAPI)
  || (IS_WEB ? _WEB_STUB_ELECTRON_API : null)
  || null;

function isDevMode(): boolean {
  try {
    if (typeof window !== 'undefined' && window.location) {
      const host = window.location.hostname;
      return host === 'localhost' || host === '127.0.0.1' || host === '';
    }
  } catch (_) {}
  return false;
}

// S4: 构建版本头（与 db.ts 保持一致的 Vite define 来源 fallback）
declare const __APP_BUILD_VERSION__: string | undefined;
declare const __APP_BUILD_TS__: string | undefined;
const RUNTIME_APP_BUILD_VERSION: string = (typeof __APP_BUILD_VERSION__ !== 'undefined' ? __APP_BUILD_VERSION__ : '0.0.0-dev');
const RUNTIME_APP_BUILD_TS: string = (typeof __APP_BUILD_TS__ !== 'undefined' ? __APP_BUILD_TS__ : '00000000000000');
function getRuntimeBuildHeaders(): Record<string, string> {
  return {
    'X-App-Build-Version': RUNTIME_APP_BUILD_VERSION,
    'X-App-Build-TS': RUNTIME_APP_BUILD_TS,
    'X-App-Side': 'shop',
    'Cache-Control': 'no-store',
  };
}

function getDefaultOrigin(): string {
  // 开发模式：走 Vite 代理（相对路径），避免 CORS
  if (isDevMode()) return '';
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin && window.location.protocol.startsWith('http')) {
      return window.location.origin.replace(/\/+$/, '');
    }
  } catch (_) { /* ignore */ }
  // 商家端统一默认域名：主域名 dtuitui.com 已 301 跳转到 yang.dtuitui.com，
  // 不再适合作为 API base，改为商家端子域名
  return 'https://shop.dtuitui.com';
}

function getRemoteApiBase(): string {
  try {
    // 开发模式：返回空字符串，调用方用 `${base}/api/...` 形成相对路径走 Vite 代理
    if (isDevMode()) return '';
    // Electron 模式：沿用 db.ts 旧默认值（用户在 localStorage 里可配）
    // Web 模式：优先用当前 origin（shop.dtuitui.com），走同域 Nginx API 代理，
    //           这样前端部署包就不用在构建时写死"服务器域名"，部署到哪台服务器都能用。
    if (IS_WEB) {
      return getDefaultOrigin();
    }
    const env = (window as any).__APP_ENV__ || {};
    const v = env.REMOTE_API_URL || env.LOCAL_API_URL
      || (window as any).REMOTE_API_URL
      || 'https://shop.dtuitui.com';
    return String(v || '').replace(/\/+$/, '');
  } catch (_) { return 'https://shop.dtuitui.com'; }
}

async function getRemoteTokenSafe(): Promise<string> {
  try {
    const t = localStorage.getItem('remote_token') || sessionStorage.getItem('remote_token');
    return t || '';
  } catch (_) { return ''; }
}

// 显式导出给 TypeScript
export const runtime = {
  mode: RUNTIME_MODE,
  isElectron: IS_ELECTRON,
  isWeb: IS_WEB,
  getSetting,
  setSetting,
  queryExpress,
  sendWecomWebhook,
  sendWecomImageWebhook,
  saveImage,
  readImage,
  existsImage,
  deleteImage,
  getPrinters,
  printWaybill,
  openExternal,
  showNotification,
  getUserDataPath,
  showOpenDialog,
  loginSuccess,
  getAppVersion,
  backup,
  _electronApi,
  /** 纯 REST 架构：本地 sql.js 持久化已废弃，save/flushSave 均为 no-op */
  db: {
    async save(): Promise<void> { /* no-op in REST-only mode */ },
    async flushSave(): Promise<void> { /* no-op */ },
  },
  /** 远程商家端后端 base URL（无尾斜杠），Web模式=当前域名，Electron=用户配置或 shop.dtuitui.com */
  getRemoteApiBase,
  /** 远程 API token（与 Authorization: Bearer <token> 对应），空字符串=未登录 */
  getRemoteTokenSafe,
  /**
   * 带鉴权地请求商家端后端 API（返回 Promise<JSON>），自动：
   *  - 拼接 base URL
   *  - 注入 Authorization 头
   *  - 非 2xx 时抛出错误，方便上层 try/catch
   */
  async api(path: string, options: RequestInit = {}): Promise<any> {
    const base = getRemoteApiBase();
    const token = await getRemoteTokenSafe();
    const url = /^https?:/.test(path) ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getRuntimeBuildHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> || {}),
    };
    const resp = await fetch(url, { ...options, headers });
    let data: any = null;
    const text = await resp.text();
    try { data = text ? JSON.parse(text) : null; } catch (_) { data = { _raw: text }; }
    if (!resp.ok || (data && (data.success === false || data.error))) {
      throw new Error(data?.error || data?.message || `HTTP ${resp.status}`);
    }
    return data;
  },
};
export default runtime;
